import { useEffect, useState } from "react";
import { Space, Table, Button, Modal, Input, Form, Upload, message } from "antd";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { cloneDeep } from "lodash-es";
import type { UploadChangeParam } from "antd/es/upload";
import type { UploadFile } from "antd/es/upload/interface";

import api from "@/api";
import { baseUrl2 } from "@/api/staging";
import { format } from "@/utils";

const { confirm } = Modal;

import dataJson from "@/../public/data.json";

const INPUT_HEIGHT = 38;
const prefix = "certificate";
import "./index.less";
import { getServiceVal } from "@/utils/cookies";


function Certificate() {
  const { add, req_err, certificate, confirmText, cancelText, delete_success, name_label } = dataJson;

  const [open, setOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [columns, setColumns] = useState(certificate.table_columns);
  const [resourceIdLoading, setResourceIdLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [form] = Form.useForm();

  const initColumns = () => {
    const tableColumnsCopy = cloneDeep(certificate.table_columns);
    const columnsCopy = tableColumnsCopy.map((item: any) => {
      if (item.key === "status") {
        item.render = (text: any, record: any) => {
          const statusText = text === "0" ? certificate.normal : certificate.abnormal;
          return <span>{statusText}</span>;
        };
      } else if (item.key === "createTime") {
        item.render = (text: any, record: any) => {
          const createTimeText = format(new Date(Number(text)), "yy-MM-dd");
          return <span>{createTimeText}</span>;
        };
      } else if (item.key === "expirationDate") {
        item.render = (text: any, record: any) => {
          const expirationDateText = format(new Date(Number(text)), "yy-MM-dd");
          return <span>{expirationDateText}</span>;
        };
      } else if (item.key === "action") {
        item.render = (text: any, record: any) => {
          const btnArr = item.btns;
          const btnShow = record.status === "0" && record.abolishFlag === "0" ? "normal" : "unNormal";
          return (
            <Space size={48}>
              {btnArr[btnShow].map((ele: any) => {
                return (
                  <a key={ele.key} onClick={() => handleClick(ele.key, record)}>
                    {ele.label}
                  </a>
                );
              })}
            </Space>
          );
        };
      }
      return item;
    });
    setColumns(columnsCopy);
  };

  const getList = async () => {
    const res: any = await api.certificateList({ page: 1, limit: 10 });
    if (res.code === 200) {
      const resData = res.data;
      setDataSource(resData.datas);
    } else {
      window.message.error(res.msg || req_err);
    }
  };

  const handleAdd = () => {
    setOpen(true);
  };

  const handleRepeal = (item: any) => {
    confirm({
      title: certificate.repeal_certificate,
      icon: null,
      width: 328,
      centered: true,
      closable: true,
      closeIcon: <CloseOutlined />,
      okText: confirmText,
      cancelText: cancelText,
      content: (
        <div className="repeal-modal-content">
          {certificate.is_repeal}&quot;{item.name}&quot;
        </div>
      ),
      onOk: async () => {
        const res: any = await api.certificateAbolish(item);
        if (res.code === 200) {
          window.message.success("废除成功");
          getList();
        } else {
          window.message.error(res.msg || req_err);
        }
      },
    });
  };

  const handleDelete = (item: any) => {
    confirm({
      title: certificate.delete_certificate,
      icon: null,
      width: 328,
      centered: true,
      closable: true,
      closeIcon: <CloseOutlined />,
      okText: confirmText,
      cancelText: cancelText,
      content: (
        <div className="delete-modal-content">
          {certificate.is_delete}&quot;{item.name}&quot;
        </div>
      ),
      onOk: async () => {
        const res: any = await api.certificateDelete(item);
        if (res.code === 200) {
          window.message.success(delete_success);
          getList();
        } else {
          window.message.error(res.msg || req_err);
        }
      },
    });
  };

  const handleDownload = async (item: any) => {
    const res: any = await api.certificateDownload(item);
    if (res.code === 500) {
      window.message.error("下载失败");
      return;
    }
    const url = window.URL.createObjectURL(new Blob([res]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", item.name + ".cer");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleClick = (type: string, record: any) => {
    if (type === "repeal") {
      handleRepeal(record);
    } else if (type === "download") {
      handleDownload(record);
    } else if (type === "delete") {
      handleDelete(record);
    }
  };

  // const beforeUpload = (file: RcFile) => {
  //   const isLt2M = file.size / 1024 / 1024 < 50;
  //   if (!isLt2M) {
  //     window.message.error("图片大小不能超过50MB");
  //   }
  //   return isLt2M;
  // };

  const resourceIdChange = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === "uploading") {
      setResourceIdLoading(true);
      return;
    } else {
      setResourceIdLoading(false);
    }
    if (info.file.status === "done") {
      setResourceIdLoading(false);
      form.setFieldValue("resourceId", info?.file?.response?.data?.id);
      form.setFieldValue("resourceName", info?.file?.name);
      form.validateFields(["resourceName"]);
    } else if (info.file.status === "error") {
      window.message.error(info.file.response.msg);
      form.setFieldValue("resourceId", "");
      form.setFieldValue("resourceName", "");
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setConfirmLoading(true);
      try {
        const res: any = await api.certificateAdd({
          name: values?.name,
          resourceId: values?.resourceId,
        });
        if (res.code === 200) {
          setOpen(false);
          setConfirmLoading(false);
          getList();
        } else {
          setConfirmLoading(false);
          window.message.error(res.msg || req_err);
        }
      } catch (errorInfo: any) {
        console.error(errorInfo);
        setConfirmLoading(false);
        window.message.error(errorInfo?.response?.data?.msg || req_err);
      }
    } catch (errorInfo: any) {
      console.error(errorInfo);
      setConfirmLoading(false);
    }
  };
  const handleCancel = () => {
    setOpen(false);
  };

  useEffect(() => {
    initColumns();
    getList();
  }, []);

  return (
    <div className={prefix}>
      <div className={`${prefix}-content`}>
        <div className={`${prefix}-content-header`}>
          <div className={`${prefix}-content-header-left`}>
            <div>{certificate.title}</div>
            <div>{certificate.tip}</div>
          </div>
          <Button type="primary" style={{ width: 90 }} onClick={handleAdd} disabled={dataSource?.length >= 2}>
            {add}
          </Button>
        </div>
        <div className={`${prefix}-content-table`}>
          <Table dataSource={dataSource} columns={columns} pagination={false} />
        </div>
      </div>
      <Modal
        title={certificate.add_certificate}
        centered
        open={open}
        width={328}
        destroyOnClose
        confirmLoading={confirmLoading}
        cancelText={cancelText}
        okText={confirmText}
        onCancel={handleCancel}
        onOk={handleOk}
        maskClosable={false}
        cancelButtonProps={{ disabled: confirmLoading }}
        afterOpenChange={(open: boolean) => !open && form.resetFields()}
        className={`${prefix}-modal`}
      >
        <Form className={`${prefix}-modal-form`} form={form} labelCol={{ span: 24 }} autoComplete="off">
          <Form.Item name="name" label={name_label} rules={[{ required: true, message: certificate.name_placeholder }]}>
            <Input
              showCount
              maxLength={100}
              placeholder={certificate.name_placeholder}
              style={{ height: INPUT_HEIGHT }}
            />
          </Form.Item>
          <div className={`${prefix}-modal-form-upload`}>
            <Form.Item
              name="resourceName"
              label={certificate.file_label}
              rules={[{ required: true, message: certificate.message_file }]}
              style={{ width: 190 }}
            >
              <Input readOnly style={{ width: 190, height: INPUT_HEIGHT }} />
            </Form.Item>
            <Form.Item name="resourceId" label=" ">
              <Upload
                action={`${baseUrl2}/file/csrUpload`}
                headers={{
                  Authorization: getServiceVal('Authorization') || ''
                }}
                data={{
                  resourceType: "DEVELOPER_CSR",
                }}
                showUploadList={false}
                // beforeUpload={beforeUpload}
                onChange={resourceIdChange}
              >
                <Button style={{ width: 80, height: INPUT_HEIGHT, marginLeft: 10 }}>
                  {resourceIdLoading ? <LoadingOutlined /> : certificate.select}
                </Button>
              </Upload>
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

export default Certificate;
